<template>
    <v-dialog v-model="dialog" width="75vw" max-width="1400px" persistent>
        <v-container fluid class="detail-page pa-0">
            <v-row no-gutters class="fill-height" style="flex-wrap: nowrap;">
                <!-- LEFT PANEL -->
                <v-col class="left-panel">
                    <!-- Header row: title + close -->
                    <div class="panel-header">
                        <div class="group-title mb-0 mt-1">{{ group.name || '' }}</div>
                    </div>
                    <!-- Driver Card -->
                    <div class="driver-card" v-if="driver">
                        <div class="driver-avatar">{{ initials }}</div>
                        <div class="driver-info">
                            <div class="driver-name">{{ driver.first_name }} {{ driver.last_name }}</div>
                            <div class="driver-shift">
                                <v-icon small color="#11A8C9">mdi-clock-outline</v-icon>
                                {{ activeShift.driver_start_time ? activeShift.driver_start_time.slice(0, 5) : '' }}
                                -
                                {{ activeShift.driver_finish_time ? activeShift.driver_finish_time.slice(0, 5) : '' }}
                            </div>
                            <!-- Live tracking badge -->
                            <div class="live-badge" v-if="isTrackingLive">
                                <span class="live-dot"></span> Live tracking
                            </div>
                        </div>
                        <v-btn small rounded color="#11A8C9" dark v-if="driver.phone" :href="'tel:' + driver.phone">
                            <v-icon small left>mdi-phone</v-icon>
                            Call
                        </v-btn>
                    </div>
                    <!-- Active route banner: shown only while viewing the Cancelled tab. -->
                    <div class="active-route-banner" v-if="activeRoute && activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]">
                        <v-btn icon x-small class="active-route-close-btn" @click="closeActiveRoute">
                            <v-icon size="12" color="white">mdi-close</v-icon>
                        </v-btn>
                        <span class="active-route-banner-text">Route #{{ activeRoute.id }} is active</span>
                    </div>
                    <!-- Tabs -->
                    <div class="status-tabs">
                        <div class="tab" :class="{ active: activeTab === TAB_KEY[ORDER_STATUS.ACTIVE] }"
                            @click="activeTab = TAB_KEY[ORDER_STATUS.ACTIVE]">
                            Pending
                        </div>
                        <div class="tab" :class="{ active: activeTab === TAB_KEY[ORDER_STATUS.IN_PROGRESS] }"
                            @click="activeTab = TAB_KEY[ORDER_STATUS.IN_PROGRESS]">
                            In Progress
                        </div>
                        <div class="tab" :class="{ active: activeTab === TAB_KEY[ORDER_STATUS.COMPLETED] }"
                            @click="activeTab = TAB_KEY[ORDER_STATUS.COMPLETED]">
                            Completed
                        </div>
                        <div class="tab" :class="{ active: activeTab === TAB_KEY[ORDER_STATUS.CANCELLED] }"
                            @click="activeTab = TAB_KEY[ORDER_STATUS.CANCELLED]">
                            Cancelled
                        </div>
                    </div>
                    <!-- Business Filter (hidden for now — re-enable by uncommenting) -->
                    <!-- <v-select dense outlined hide-details clearable placeholder="All Businesses" :items="businesses"
                        v-model="selectedBusiness" class="business-filter mb-2" /> -->
                    <!-- Route Filter (shown on all tabs except Cancelled) -->
                    <v-select v-if="activeTab !== TAB_KEY[ORDER_STATUS.CANCELLED]" dense outlined hide-details clearable
                        placeholder="All Routes" :items="routesList" item-text="text" item-value="value"
                        v-model="selectedRoute" class="business-filter route-filter" />

                    <!-- Completed tab: deliveries done + estimated amount due to the driver -->
                    <div v-if="activeTab === TAB_KEY[ORDER_STATUS.COMPLETED] && completedDeliveries.length"
                        class="deliveries-summary">
                        <span>{{ completedDeliveries.length }} {{ completedDeliveries.length === 1 ? 'delivery' : 'deliveries' }} done</span>
                        <span v-if="deliveriesDueTotal != null" class="deliveries-due">{{ formatMoney(deliveriesDueTotal) }} due</span>
                    </div>

                    <!-- CANCELLED TAB: unchanged order-card listing (an order's stops cancel together, so no meaningful sequence) -->
                    <template v-if="activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]">
                        <template v-if="filteredOrders.length">
                            <div v-for="order in filteredOrders" :key="order.id" class="order-card"
                                :class="{ 'order-card--selected': selectedOrderIds.includes(order.id) }"
                                @click="toggleOrder(order.id)">
                                <div class="order-header">
                                    <div class="order-id">
                                        Order #{{ order.id }}
                                        <span class="order-time" v-if="order.created_at">{{ formatTime(order.created_at) }}</span>
                                    </div>
                                    <div class="order-header-right">
                                        <div class="order-status" :class="statusClass(getOrderDisplayStatus(order))">
                                            {{ statusLabel(getOrderDisplayStatus(order)) }}
                                        </div>
                                    </div>
                                </div>

                                <!-- NOTE: orders can arrive without a stops array (socket-built or
                                     synthesised on cancel), so every lookup is guarded. -->
                                <div v-if="orderNote(order, 'pickup')" class="d-flex align-center order-date">
                                    <i title="Pickup note" class="mdi mdi-note-text-outline light-blue--text mr-2"></i>
                                    <span class="blue-grey--text">{{ orderNote(order, 'pickup') }}</span>
                                </div>

                                <div v-if="orderNote(order, 'dropoff')" class="d-flex align-center order-date">
                                    <i title="Delivery Note" class="mdi mdi-note-text-outline orange--text mr-2"></i>
                                    <span class="blue-grey--text">{{ orderNote(order, 'dropoff') }}</span>
                                </div>

                                <!-- Pickup -->
                                <div class="location-row">
                                    <img :src="pickupIcon" class="location-icon" alt="Pickup" />
                                    <div>
                                        <div class="location-label">Pickup</div>
                                        <div class="location-value">
                                            {{ restaurantsMap[order.restaurant_id]
                                                ? restaurantsMap[order.restaurant_id].name
                                                : '—' }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Delivery -->
                                <div class="location-row">
                                    <img :src="deliveryIcon" class="location-icon" alt="Delivery" />
                                    <div>
                                        <div class="location-label">Delivery</div>
                                        <div class="location-value">
                                            {{ order.delivery_address || (order.delivery_lat + ', ' + order.delivery_lng) }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Footer -->
                                <div class="order-footer">
                                    <div class="stat-box">
                                        <strong>{{ order.order_quantity }}</strong>
                                        <span>Total Items</span>
                                    </div>
                                    <div class="stat-box">
                                        <strong>£{{ restaurantsMap[order.restaurant_id]
                                            ? restaurantsMap[order.restaurant_id].standard_pay
                                            : 0 }}</strong>
                                        <span>Amount</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div v-else class="no-orders">
                            No orders found.
                        </div>
                    </template>

                    <!-- ALL OTHER TABS: route-grouped, sequence-ordered stop listing -->
                    <template v-else>
                        <template v-if="displayGroups.length">
                            <div v-for="group in displayGroups" :key="group.route.id" class="route-group">
                                <div class="route-header" @click="toggleRouteVisibility(group.route.id)">
                                    <span>Route #{{ group.route.id }}</span>
                                    <div class="d-flex align-center">
                                        <v-btn v-if="canReorderRoute(group) && !isReordering(group.route.id)" icon x-small
                                            class="route-reorder-btn" title="Reorder stops"
                                            @click.stop="startReorder(group)">
                                            <v-icon size="16">mdi-swap-vertical</v-icon>
                                        </v-btn>
                                        <v-icon size="20" class="route-toggle-icon">
                                            {{ isRouteVisible(group.route.id) ? 'mdi-eye-outline' : 'mdi-eye-off-outline' }}
                                        </v-icon>
                                    </div>
                                </div>

                                <!-- Reorder mode banner: save/cancel the in-progress draft order -->
                                <div v-if="isReordering(group.route.id)" class="reorder-banner">
                                    <span>
                                        Drag to reorder — tap a stop to locate it on the map.
                                        <template v-if="reorderLockedCount">
                                            In-progress stops stay at the top.
                                        </template>
                                        Nothing saves until you confirm.
                                    </span>
                                    <div class="reorder-banner-actions">
                                        <v-btn x-small text @click="cancelReorder" :disabled="reorderSaving">Cancel</v-btn>
                                        <v-btn x-small color="#11A8C9" dark @click="saveReorder(group)" :loading="reorderSaving">
                                            Save order
                                        </v-btn>
                                    </div>
                                </div>

                                <template v-if="isRouteVisible(group.route.id)">
                                <template v-for="(stop, stopIndex) in stopsForDisplay(group)">
                                <div :key="stop.id || (stop.order_id + '-' + stop.type)"
                                    class="order-card stop-card"
                                    :class="[stop.type === 'pickup' ? 'stop-card--pickup' : 'stop-card--dropoff',
                                        { 'order-card--selected': selectedOrderIds.includes(stop.order_id),
                                          'stop-card--reordering': isReordering(group.route.id),
                                          'stop-card--locked': isLockedStop(group, stopIndex),
                                          'stop-card--focused': focusedStopKey === stopKey(stop),
                                          'stop-card--drop-target': isDropTarget(group, stopIndex),
                                          'stop-card--drop-blocked': isDropBlocked(group, stopIndex) }]"
                                    :data-stop-key="stopKey(stop)"
                                    :draggable="isStopDraggable(group, stopIndex)"
                                    @dragstart="isStopDraggable(group, stopIndex) && onStopDragStart(stopIndex, $event)"
                                    @dragover="isReordering(group.route.id) && onStopDragOver($event, stopIndex)"
                                    @drop="isReordering(group.route.id) && onStopDrop(stopIndex, $event)"
                                    @dragend="isReordering(group.route.id) && onStopDragEnd()"
                                    @click="onStopCardClick(group, stop)">

                                    <!-- Drop indicator shown on the card the dragged stop would land on -->
                                    <div class="drop-here-badge" v-if="isDropTarget(group, stopIndex)">
                                        <v-icon size="11" color="#fff">mdi-arrow-down-bold</v-icon>
                                        DROP HERE
                                    </div>

                                    <!-- Shown instead when the drop would break the in-progress rule -->
                                    <div class="drop-blocked-badge" v-if="isDropBlocked(group, stopIndex)">
                                        <v-icon size="11" color="#fff">mdi-cancel</v-icon>
                                        CAN'T DROP HERE
                                    </div>

                                    <!-- Side ribbon: vertical PICKUP / DROP OFF label so the card's role is clear without eating vertical space -->
                                    <div class="stop-side-ribbon"
                                        :class="stop.type === 'pickup' ? 'stop-type-pickup' : 'stop-type-dropoff'">
                                        <span class="stop-side-ribbon-text">{{ stop.type === 'pickup' ? 'PICKUP' : 'DROP OFF' }}</span>
                                    </div>

                                    <!-- Top row: drag handle / lock (reorder mode) — created time — order number — completed time -->
                                    <div class="stop-top-row">
                                        <v-icon v-if="isStopDraggable(group, stopIndex)" size="18" color="#0f2d74"
                                            class="stop-drag-handle">
                                            mdi-drag-vertical
                                        </v-icon>
                                        <v-icon v-else-if="isLockedStop(group, stopIndex)" size="16" color="#b45309"
                                            class="stop-lock-icon" title="In progress — position fixed">
                                            mdi-lock-outline
                                        </v-icon>
                                        <div class="stop-time-badge stop-time-badge--created" v-if="stop.created_at">
                                            {{ formatTime(stop.created_at) }}
                                        </div>
                                        <div class="stop-order-id">
                                            <span class="stop-seq-badge" v-if="stop.sequence != null">{{ stop.sequence }}</span>
                                            Order #{{ stop.order_id }}
                                        </div>
                                        <div class="stop-id">
                                            Stop #{{ stop.id }}
                                        </div>
                                        <div class="stop-time-badge stop-time-badge--completed" v-if="stop.completed_at">
                                            {{ formatTime(stop.completed_at) }}
                                        </div>
                                    </div>

                                    <div v-if="stop.delivery_note" class="d-flex align-center order-date">
                                        <i title="Note" class="mdi mdi-note-text-outline mr-2"
                                            :class="stop.type === 'pickup' ? 'light-blue--text' : 'orange--text'"></i>
                                        <span class="blue-grey--text">{{ stop.delivery_note }}</span>
                                    </div>

                                    <!-- Location + status, now with more room since the type badge moved up top -->
                                    <div class="location-row stop-location-row">
                                        <img :src="stop.type === 'pickup' ? pickupIcon : deliveryIcon" class="location-pin-icon"
                                            :alt="stop.type === 'pickup' ? 'Pickup' : 'Drop off'" />
                                        <div class="location-value stop-location-value" v-if="stop.type === 'pickup'">
                                            {{ stopRestaurantName(stop) }}
                                        </div>
                                        <div class="location-value stop-location-value" v-else>
                                            {{ (stop.order && stop.order.delivery_address) || (stop.latitude + ', ' + stop.longitude) }}
                                        </div>

                                        <div class="stop-row-right">
                                            <v-icon v-if="isReordering(group.route.id)" size="16" color="#11A8C9"
                                                class="stop-locate-icon" title="Show on map">
                                                mdi-map-marker-radius-outline
                                            </v-icon>
                                            <div class="order-status" :class="statusClass(stop.status)">
                                                {{ stopStatusLabel(stop) }}
                                            </div>
                                            <v-menu offset-y left v-if="(activeTab === TAB_KEY[ORDER_STATUS.ACTIVE] || activeTab === TAB_KEY[ORDER_STATUS.IN_PROGRESS]) && stop.order && !isReordering(group.route.id)">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-btn icon small class="order-menu-btn" v-bind="attrs" v-on="on"
                                                        @click.stop>
                                                        <v-icon size="20">mdi-dots-vertical</v-icon>
                                                    </v-btn>
                                                </template>
                                                <v-list dense>
                                                    <v-list-item @click="confirmCancelOrder(stop.order)">
                                                        <v-list-item-icon class="mr-2">
                                                            <v-icon color="red" size="18">mdi-close-circle-outline</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title class="red--text">Cancel Order</v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </div>
                                    </div>
                                    <div v-if="!stop.completed_at && stop.eta" class="stop-eta-row">
                                        <v-icon size="14" color="#0f2d74">mdi-timer-outline</v-icon>
                                        <span>ETA {{ formatEta(stop) }}</span>
                                    </div>
                                    <!-- Driver earning for this order (on the dropoff only, so it isn't shown twice) -->
                                    <div v-if="stop.type !== 'pickup' && stop.order && stop.order.order_total != null"
                                        class="stop-earning-row">
                                        <v-icon size="14" color="#1b7f3b">mdi-cash</v-icon>
                                        <span>{{ formatMoney(stop.order.order_total) }}</span>
                                    </div>
                                </div>

                                <!-- Divider marking where the locked in-progress block ends -->
                                <div :key="'divider-' + (stop.id || stopIndex)" class="reorder-lock-divider"
                                    v-if="isLockDividerAfter(group, stopIndex)">
                                    <span>Stops below can be reordered</span>
                                </div>
                                </template>
                                </template>
                            </div>
                        </template>
                        <div v-else class="no-orders">
                            No orders found.
                        </div>
                    </template>
                </v-col>
                <!-- RIGHT PANEL — inlined map -->
                <v-col class="map-panel">
                    <div class="map-container">
                        <div class="map-close-btn">
                            <v-btn icon small color="white" elevation="3" @click="$emit('close')">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <div ref="map" class="real-map"></div>
                        <!-- Legend -->
                        <div class="map-legend">
                            <div class="legend-item"><span class="dot orange"></span> Restaurant</div>
                            <div class="legend-item"><span class="dot gray"></span> Waiting</div>
                            <div class="legend-item"><span class="dot blue"></span> Pickup</div>
                            <div class="legend-item"><span class="dot green"></span> Delivery</div>
                            <div class="legend-item" v-if="isTrackingLive">
                                <span class="dot purple pulse"></span> Driver (live)
                            </div>
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </v-dialog>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import pickupIcon from '@/assets/pickup.svg'
import deliveryIcon from '@/assets/delivery.svg'
import restaurantIconSvg from '@/assets/restaurant-marker.svg'
import waitingIconSvg from '@/assets/waiting-location-marker.svg'
import pickupIconSvg from '@/assets/pickup.svg'
import deliveryIconSvg from '@/assets/delivery.svg'
import liveIconSvg from '@/assets/live.png'

// Status constants
const ROUTE_STATUS = {
    ACTIVE: 10,
    FINISHED: 20,
}
const ROUTE_PROGRESS_STATUS = {
    PENDING: 10,
    ACTIVE: 20,
    COMPLETED: 30,
}
const ORDER_STATUS = {
    ACTIVE: 30,
    IN_PROGRESS: 35,
    COMPLETED: 40,
    CANCELLED: 50,
}
const TIMESHEET_STATUS = {
    IN_WORK: 10,
    SUBMITTED: 20,
    PAY_APPROVED: 40,
    TIME_APPROVED: 50,
    DISCARDED: 60,
    DISCARDED_BY_DRIVER: 70,
}

// Maps a numeric status
const TAB_KEY = {
    [ORDER_STATUS.ACTIVE]: 'active',
    [ORDER_STATUS.IN_PROGRESS]: 'inprogress',
    [ORDER_STATUS.COMPLETED]: 'completed',
    [ORDER_STATUS.CANCELLED]: 'cancelled',
}

const STATUS_LABEL = {
    [ORDER_STATUS.ACTIVE]: 'Active',
    [ORDER_STATUS.IN_PROGRESS]: 'In Progress',
    [ORDER_STATUS.COMPLETED]: 'Completed',
    [ORDER_STATUS.CANCELLED]: 'Cancelled',
}

const STATUS_CLASS = {
    [ORDER_STATUS.ACTIVE]: 'status-active',
    [ORDER_STATUS.IN_PROGRESS]: 'status-inprogress',
    [ORDER_STATUS.COMPLETED]: 'status-completed',
    [ORDER_STATUS.CANCELLED]: 'status-cancelled',
}
const PULSE_COLOR = {
    restaurant: 'rgba(255, 72, 0, 1)', // orange
    dropoff: 'rgba(19, 255, 0, 1)',  // green
}
const LIVE_POLL_INTERVAL_MS = 15000
// Auto-scroll of the stop list while dragging a stop
const DRAG_SCROLL_EDGE_PX = 80     // distance from top/bottom edge where scrolling starts
const DRAG_SCROLL_MAX_SPEED = 18   // px per frame at the very edge
const FOCUS_REVEAL_DELAY_MS = 400
const FOCUS_MIN_ZOOM = 15

export default {
    name: 'DriverShareScheduleOrdersModal',
    props: {
        shift: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            dialog: true,
            activeTab: TAB_KEY[ORDER_STATUS.ACTIVE],
            selectedBusiness: null,
            selectedRoute: null,
            selectedOrderIds: [],
            collapsedRouteIds: [],
            reorderRouteId: null,
            reorderDraftStops: [],
            reorderSaving: false,
            reorderDragFromIndex: null,
            reorderDragOverIndex: null,
            reorderSequenceOffset: 0,
            reorderLockedCount: 0,
            focusedStopKey: null,
            pickupIcon,
            deliveryIcon,
            ORDER_STATUS,
            TIMESHEET_STATUS,
            TAB_KEY,
            map: null,
            mapMarkers: [],
            allDropoffEntries: [],
            restaurantMarkerIndex: new Map(),
            stopMarkerIndex: new Map(),
            restaurantIcon: null,
            waitingIcon: null,
            pickupMarkerIcon: null,
            deliveryMarkerIcon: null,
            liveIcon: null,
            liveMarker: null,
            liveLocationInterval: null,
            isTrackingLive: false
        }
    },

    computed: {
        group() {
            return this.shift && this.shift.restaurant_group
                ? this.shift.restaurant_group
                : {}
        },
        activeShift() {
            return this.shift || {}
        },
        driver() {
            return this.shift && this.shift.driver ? this.shift.driver : null
        },
        activeRoute() {
            if (!this.shift || !this.shift.routes) return null
            return this.shift.routes.find(
                route => Number(route.status_in_progress) === ROUTE_PROGRESS_STATUS.ACTIVE
            ) || null
        },
        orders() {
            return this.shift && this.shift.orders ? this.shift.orders : []
        },
        isDriverWorking() {
            return (
                this.shift &&
                this.shift.timesheet &&
                this.shift.timesheet.status_progress === TIMESHEET_STATUS.IN_WORK
            )
        },
        isPulsingTab() {
            return (
                this.activeTab === TAB_KEY[ORDER_STATUS.IN_PROGRESS] ||
                this.activeTab === TAB_KEY[ORDER_STATUS.COMPLETED]
            )
        },
        cancelledRouteStops() {
            return this.filteredOrders
                .flatMap(order => (order.stops || []).map(stop => ({ ...stop })))
                .sort((a, b) => a.sequence - b.sequence)
        },
        cancelledDropoffStops() {
            return this.cancelledRouteStops.filter(stop => stop.type !== 'pickup')
        },
        filteredOrders() {
            let result = this.orders.filter(order => {
                return this.getOrderTab(order) === this.activeTab
            })

            if (this.selectedBusiness) {
                result = result.filter(order => {
                    const r = this.restaurantsMap[order.restaurant_id]
                    return r && r.name === this.selectedBusiness
                })
            }

            return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        },
        ordersSignature() {
            return this.orders
                .map(order => {
                    const stopsSig = (order.stops || [])
                        .map(s => `${s.id}:${s.status}:${s.latitude}:${s.longitude}:${s.sequence}`)
                        .join(',')
                    return `${order.id}:${order.status}:${order.restaurant_id}:${stopsSig}`
                })
                .join('|')
        },
        routesSignature() {
            return ((this.shift && this.shift.routes) || [])
                .map(route => {
                    const stopsSig = (route.stops || [])
                        .map(s => `${s.id}:${s.status}:${s.latitude}:${s.longitude}:${s.sequence}`)
                        .join(',')
                    return `${route.id}:${route.status}:${route.status_in_progress}:${stopsSig}`
                })
                .join('|')
        },
        // Lookup of order objects by id, so a route's stops can carry their parent order's info
        ordersById() {
            const map = {}
            this.orders.forEach(order => {
                map[order.id] = order
            })
            return map
        },
        visibleStops() {
            if (this.activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]) return []
            if (!this.shift || !this.shift.routes) return []

            let routes = this.shift.routes
            if (this.selectedRoute) {
                routes = routes.filter(r => r.id === this.selectedRoute)
            }

            let stops = routes.flatMap(route =>
                (route.stops || [])
                    .filter(stop => Number(stop.status) !== ORDER_STATUS.CANCELLED)
                    .filter(stop => this.getStopTabKey(route, stop) === this.activeTab)
                    .map(stop => ({ ...stop, route, order: this.ordersById[stop.order_id] }))
            )

            if (this.selectedBusiness) {
                stops = stops.filter(stop => {
                    const r = stop.order ? this.restaurantsMap[stop.order.restaurant_id] : null
                    return r && r.name === this.selectedBusiness
                })
            }

            return stops
        },
        routesList() {
            if (!this.shift || !this.shift.routes) return []
            if (this.activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]) return []

            return this.shift.routes
                .filter(route =>
                    (route.stops || []).some(stop =>
                        Number(stop.status) !== ORDER_STATUS.CANCELLED &&
                        this.getStopTabKey(route, stop) === this.activeTab
                    )
                )
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .map(route => ({
                    text: `Route #${route.id}`,
                    value: route.id,
                }))
        },
        routeGroups() {
            if (this.activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]) return []
            if (!this.shift) return []

            const groupsByRouteId = new Map()
            this.visibleStops.forEach(stop => {
                const routeId = stop.route.id
                if (!groupsByRouteId.has(routeId)) {
                    groupsByRouteId.set(routeId, { route: stop.route, stops: [] })
                }
                groupsByRouteId.get(routeId).stops.push(stop)
            })

            return [...groupsByRouteId.values()]
                .map(grp => {
                    grp.stops.sort((a, b) => {
                        const sa = a.sequence == null ? Number.MAX_SAFE_INTEGER : a.sequence
                        const sb = b.sequence == null ? Number.MAX_SAFE_INTEGER : b.sequence
                        if (sa !== sb) return sa - sb
                        return new Date(a.created_at) - new Date(b.created_at)
                    })
                    return grp
                })
                .sort((a, b) => new Date(b.route.created_at) - new Date(a.route.created_at))
        },
        // What the template renders for the non-Cancelled tabs.
        displayGroups() {
            return this.routeGroups
        },
        restaurantsMap() {
            const map = {}
            if (!this.shift || !this.shift.shifts) return map
            this.shift.shifts.forEach(s => {
                if (s.restaurant) map[s.restaurant.id] = s.restaurant
            })
            return map
        },
        initials() {
            if (!this.driver) return ''
            return (
                (this.driver.first_name || '').charAt(0) +
                (this.driver.last_name || '').charAt(0)
            ).toUpperCase()
        },
        // Orders whose dropoff is completed (one entry per order)
        completedDeliveries() {
            const seen = new Set()
            const list = []
            ;((this.shift && this.shift.routes) || []).forEach(route => {
                ;(route.stops || []).forEach(stop => {
                    if (stop.type === 'pickup') return
                    if (Number(stop.status) !== ORDER_STATUS.COMPLETED) return
                    const orderId = Number(stop.order_id)
                    if (seen.has(orderId)) return
                    seen.add(orderId)
                    list.push(this.ordersById[orderId] || { id: orderId })
                })
            })
            return list
        },
        // Total estimated earning for completed deliveries; null if none known yet
        deliveriesDueTotal() {
            const amounts = this.completedDeliveries
                .map(order => order.order_total)
                .filter(v => v != null && !isNaN(Number(v)))
            if (!amounts.length) return null
            return amounts.reduce((sum, v) => sum + Number(v), 0)
        },
        businesses() {
            return Object.values(this.restaurantsMap).map(r => r.name)
        },
    },

    mounted() {
        this._activePopupMarker = null
        this._focusTimer = null
        this.$nextTick(() => {
            this.initIcons()
            this.initMap()
            this.renderMarkers()
            if (this.isDriverWorking) {
                this.startLiveTracking()
            }
        })
    },

    beforeDestroy() {
        this.stopDragAutoScroll()
        this.stopLiveTracking()
        if (this._focusTimer) {
            clearTimeout(this._focusTimer)
            this._focusTimer = null
        }
        if (this.map) {
            this.map.off('moveend zoomend', this.syncVisibleDropoffMarkers)
            this.map.remove()
            this.map = null
        }
    },

    watch: {

        'shift.timesheet.status_progress'(status) {
            if (status === TIMESHEET_STATUS.IN_WORK) {
                this.startLiveTracking()
            } else {
                this.stopLiveTracking()
            }
        },
        ordersSignature() {
            this.renderMarkers()
        },
        // Route stop changes (create, cancel, reorder) come through here
        routesSignature() {
            this.renderMarkers()
        },
        selectedBusiness() {
            this.renderMarkers()
        },
        selectedRoute() {
            this.renderMarkers()
        },
        activeTab() {
            this.selectedOrderIds = []
            this.selectedRoute = null
            this.resetReorderState()
            this.renderMarkers()
        },
        // Selection no longer triggers a full rebuild — see toggleOrder().
    },

    methods: {
        async fetchLiveLocation() {
            if (!this.driver || !this.map) return

            try {
                await this.$store.dispatch('fetchDriverLocation', String(this.driver.id))

                const locations = this.$store.getters['driverLocations']

                const pos = locations ? locations[this.driver.id] : null

                if (pos && pos.latitude && pos.longitude) {
                    const latlng = [Number(pos.latitude), Number(pos.longitude)]

                    if (this.liveMarker) {
                        // Smoothly reposition existing marker — no flicker
                        this.liveMarker.setLatLng(latlng)
                    } else {
                        // First fix: create the marker
                        this.liveMarker = L.marker(latlng, { icon: this.liveIcon, zIndexOffset: 1000 })
                            .addTo(this.map)
                            .bindPopup(
                                `<strong>${this.driver.first_name} ${this.driver.last_name}</strong><br>Live location`
                            )
                        this.bindHoverPopup(this.liveMarker)
                    }
                }
            } catch (err) {
                console.warn('[DriverModal] Live location fetch failed:', err)
            }
        },
        startLiveTracking() {
            // Don't start a second interval if already running
            if (this.liveLocationInterval) return

            this.isTrackingLive = true
            this.fetchLiveLocation()   // immediate first fetch on modal open

            this.liveLocationInterval = setInterval(() => {
                this.fetchLiveLocation()
            }, LIVE_POLL_INTERVAL_MS)
        },
        //Stop polling and remove the live marker from the map.
        stopLiveTracking() {
            if (this.liveLocationInterval) {
                clearInterval(this.liveLocationInterval)
                this.liveLocationInterval = null
            }

            if (this.liveMarker && this.map) {
                this.map.removeLayer(this.liveMarker)
                this.liveMarker = null
            }

            this.isTrackingLive = false
        },
        // Safe note lookup — orders may arrive with no stops array at all
        orderNote(order, type) {
            const stop = ((order && order.stops) || []).find(s => s.type === type)
            return stop ? stop.delivery_note : null
        },
        // Socket stops carry no restaurant_id, so fall back to the parent order
        stopRestaurantId(stop) {
            if (!stop) return null
            return stop.restaurant_id || (stop.order && stop.order.restaurant_id) || null
        },
        stopRestaurantName(stop) {
            const rid = this.stopRestaurantId(stop)
            const restaurant = rid ? this.restaurantsMap[rid] : null
            return restaurant ? restaurant.name : '—'
        },
        getStopTabKey(route, stop) {
            if (!route) return null
            const progress = Number(route.status_in_progress)

            if (progress === ROUTE_PROGRESS_STATUS.PENDING) {
                return TAB_KEY[ORDER_STATUS.ACTIVE]
            }
            if (progress === ROUTE_PROGRESS_STATUS.COMPLETED) {
                return TAB_KEY[ORDER_STATUS.COMPLETED]
            }
            if (progress === ROUTE_PROGRESS_STATUS.ACTIVE) {
                return Number(stop.status) === ORDER_STATUS.COMPLETED
                    ? TAB_KEY[ORDER_STATUS.COMPLETED]
                    : TAB_KEY[ORDER_STATUS.IN_PROGRESS]
            }
            return null
        },
        getOrderTab(order) {
            const { CANCELLED } = ORDER_STATUS
            const stops = order.stops || []
            const stopStatuses = stops.map(stop => Number(stop.status))

            if (
                Number(order.status) === CANCELLED ||
                (stopStatuses.length > 0 && stopStatuses.every(s => s === CANCELLED))
            ) {
                return TAB_KEY[CANCELLED]
            }

            return null
        },
        getOrderDisplayStatus(order) {
            return ORDER_STATUS.CANCELLED
        },

        // map
        initIcons() {
            this.restaurantIcon = L.icon({
                iconUrl: restaurantIconSvg,
                iconSize: [62, 62],
                iconAnchor: [21, 42],
                popupAnchor: [0, -38],
            })
            this.waitingIcon = L.icon({
                iconUrl: waitingIconSvg,
                iconSize: [62, 62],
                iconAnchor: [25, 50],
                popupAnchor: [0, -45],
            })
            this.pickupMarkerIcon = L.icon({
                iconUrl: pickupIconSvg,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                popupAnchor: [0, -12],
            })
            this.deliveryMarkerIcon = L.icon({
                iconUrl: deliveryIconSvg,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                popupAnchor: [0, -12],
            })
            this.liveIcon = L.icon({
                iconUrl: liveIconSvg,
                iconSize: [45, 45],
                iconAnchor: [22, 45],
                popupAnchor: [0, -40],
            })
        },

        initMap() {
            if (!this.$refs.map) return
            this.map = L.map(this.$refs.map, { zoomControl: false }).setView([51.505, -0.09], 13)
            L.control.zoom({ position: 'bottomright' }).addTo(this.map)
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map)
            this.map.on('moveend zoomend', this.syncVisibleDropoffMarkers)
        },

        clearMarkers() {
            this.mapMarkers.forEach(m => this.map.removeLayer(m))
            this.mapMarkers = []
            this.allDropoffEntries = []
            this.restaurantMarkerIndex.clear()
            this.stopMarkerIndex.clear()
        },

        buildRestaurantPopupHtml(restaurant, entries) {
            const sorted = [...(entries || [])].sort((a, b) => {
                const sa = a.pickupSeq != null ? a.pickupSeq : 0
                const sb = b.pickupSeq != null ? b.pickupSeq : 0
                return sa - sb
            })

            const lines = sorted.map(e => {
                const pSeq = e.pickupSeq != null ? e.pickupSeq : '-'
                if (!e.dropoff) {
                    return `Pickup #${pSeq} → Dropoff: no info`
                }
                const dSeq = e.dropoff.sequence != null ? e.dropoff.sequence : '-'
                const lat = e.dropoff.latitude != null ? e.dropoff.latitude : '-'
                const lng = e.dropoff.longitude != null ? e.dropoff.longitude : '-'
                return `Pickup #${pSeq} → Dropoff #${dSeq} · Lat: ${lat} · Lng: ${lng}`
            })

            return `
                <div style="font-size:12px;line-height:1.6;">
                    <strong>${restaurant.name}</strong>
                    ${lines.length ? '<br>' + lines.join('<br>') : ''}
                </div>
            `
        },

        setMarkerPriority(marker, isPriority) {
            if (!marker) return
            const el = marker.getElement && marker.getElement()
            if (!el) return

            if (isPriority) {
                if (el.dataset.origZIndex === undefined) {
                    el.dataset.origZIndex = el.style.zIndex || ''
                }
                el.style.zIndex = 9000
            } else if (el.dataset.origZIndex !== undefined) {
                el.style.zIndex = el.dataset.origZIndex
                delete el.dataset.origZIndex
            }
        },

        bindHoverPopup(marker) {
            if (!marker) return
            marker.on('mouseover', () => {
                if (this._activePopupMarker && this._activePopupMarker !== marker) {
                    this._activePopupMarker.closePopup()
                }
                this._activePopupMarker = marker
                marker.openPopup()
            })
            marker.on('mouseout', () => {
                marker.closePopup()
                if (this._activePopupMarker === marker) {
                    this._activePopupMarker = null
                }
            })
        },

        buildRestaurantIcon(entries) {
            const sortedEntries = [...(entries || [])].sort((a, b) => {
                const sa = a.pickupSeq != null ? a.pickupSeq : 0
                const sb = b.pickupSeq != null ? b.pickupSeq : 0
                return sa - sb
            })

            const anySelected = sortedEntries.some(e => this.selectedOrderIds.includes(e.orderId))

            const badgesHtml = sortedEntries
                .map(e => {
                    const label = e.pickupSeq != null ? e.pickupSeq : '-'
                    const isSelected = this.selectedOrderIds.includes(e.orderId)
                    return `<div class="pickup-sequence-badge${isSelected ? ' badge-selected' : ''}">${label}</div>`
                })
                .join('')

            return L.divIcon({
                className: 'custom-pin-marker',
                html: `
                    <div class="pin-marker-wrap${anySelected ? ' marker-has-selected' : ''}" style=" --pulse-color:${PULSE_COLOR.restaurant};">
                            <img src="${restaurantIconSvg}" class="pin-marker-img">
                            ${badgesHtml ? `<div class="pickup-sequence-row">${badgesHtml}</div>` : ''}
                    </div>
                `,
                iconSize: [46, 58],
                iconAnchor: [23, 58],
                popupAnchor: [0, -58]
            })
        },

        buildDropoffIcon(stop, selected) {
            const baseWidth = 46
            const baseHeight = 58
            const scale = selected ? 1.35 : 1
            const width = Math.round(baseWidth * scale)
            const height = Math.round(baseHeight * scale)
            const iconImgSize = Math.round(32 * scale)
            const iconLeft = Math.round(7 * scale)
            const iconTop = Math.round(12 * scale)
            return L.divIcon({
                className: 'custom-stop-marker',
                html: `
                    <div
                        class="stop-marker ${selected ? 'marker-pulse marker-selected' : ''}"
                        style="
                            --pulse-color:${PULSE_COLOR.dropoff};
                            width:${width}px;
                            height:${height}px;
                        ">
                        <img
                            src="${deliveryIconSvg}"
                            class="stop-icon"
                            style="
                                width:${iconImgSize}px;
                                height:${iconImgSize}px;
                                left:${iconLeft}px;
                                top:${iconTop}px;
                            "
                        />
                        ${stop.sequence != null ? `<div class="sequence-badge${selected ? ' sequence-badge--selected' : ''}">${stop.sequence}</div>` : ''}
                    </div>
                `,
                iconSize: [width, height],
                iconAnchor: [width / 2, height],
                popupAnchor: [0, -height]
            })
        },
        renderMarkers() {
            if (!this.map) return
            this.clearMarkers()
            const all = []
            const group = this.group
            const isCancelledTab = this.activeTab === TAB_KEY[ORDER_STATUS.CANCELLED]

            // Aggregate restaurants
            const restaurantAgg = {}

            if (isCancelledTab) {
                this.filteredOrders.forEach(order => {
                    const rid = order.restaurant_id
                    const restaurant = this.restaurantsMap[rid]

                    if (!restaurant || !restaurant.map_latitude || !restaurant.map_longitude) {
                        return
                    }
                    if (!restaurantAgg[rid]) {
                        restaurantAgg[rid] = {
                            restaurant,
                            orderIds: [],
                            entries: []   // [{ orderId, pickupSeq, dropoff }]
                        }
                    }
                    restaurantAgg[rid].orderIds.push(order.id)

                    const pickupStop = (order.stops || []).find(stop => stop.type === 'pickup')
                    const dropoffStop = (order.stops || []).find(stop => stop.type !== 'pickup')

                    if (pickupStop) {
                        restaurantAgg[rid].entries.push({
                            orderId: order.id,
                            pickupSeq: pickupStop.sequence,
                            dropoff: dropoffStop
                                ? {
                                    sequence: dropoffStop.sequence,
                                    latitude: dropoffStop.latitude,
                                    longitude: dropoffStop.longitude,
                                }
                                : null,
                        })
                    }
                })
            } else {
                this.visibleStops
                    .filter(stop => stop.type === 'pickup')
                    .forEach(stop => {
                        // Socket-delivered stops have no restaurant_id — fall back to the order
                        const rid = this.stopRestaurantId(stop)
                        const restaurant = rid ? this.restaurantsMap[rid] : null

                        if (!restaurant || !restaurant.map_latitude || !restaurant.map_longitude) {
                            return
                        }
                        if (!restaurantAgg[rid]) {
                            restaurantAgg[rid] = {
                                restaurant,
                                orderIds: [],
                                entries: []
                            }
                        }
                        restaurantAgg[rid].orderIds.push(stop.order_id)

                        const dropoffStop = ((stop.route && stop.route.stops) || [])
                            .find(s => +s.order_id === +stop.order_id && s.type !== 'pickup')

                        restaurantAgg[rid].entries.push({
                            orderId: stop.order_id,
                            pickupSeq: stop.sequence,
                            dropoff: dropoffStop
                                ? {
                                    sequence: dropoffStop.sequence,
                                    latitude: dropoffStop.latitude,
                                    longitude: dropoffStop.longitude,
                                }
                                : null,
                        })
                    })
            }

            // Restaurant markers
            Object.values(restaurantAgg).forEach(({ restaurant, orderIds, entries }) => {
                const lat = Number(restaurant.map_latitude)
                const lng = Number(restaurant.map_longitude)
                if (isNaN(lat) || isNaN(lng)) return

                const icon = this.buildRestaurantIcon(entries)

                const marker = L.marker([lat, lng], { icon })
                    .addTo(this.map)
                    .bindPopup(this.buildRestaurantPopupHtml(restaurant, entries))

                this.bindHoverPopup(marker)
                this.setMarkerPriority(marker, entries.some(e => this.selectedOrderIds.includes(e.orderId)))

                this.mapMarkers.push(marker)
                this.restaurantMarkerIndex.set(restaurant.id, { marker, orderIds, entries })
                all.push([lat, lng])
            })

            const dropoffSource = isCancelledTab
                ? this.cancelledDropoffStops
                : this.visibleStops.filter(stop => stop.type !== 'pickup')

            dropoffSource.forEach(stop => {
                const lat = Number(stop.latitude)
                const lng = Number(stop.longitude)
                if (isNaN(lat) || isNaN(lng)) return

                this.allDropoffEntries.push({ orderId: stop.order_id, stop, lat, lng })
                all.push([lat, lng])
            })

            // Waiting location — always show
            if (group && group.waiting_lat && group.waiting_lng) {
                const lat = Number(group.waiting_lat)
                const lng = Number(group.waiting_lng)
                if (!isNaN(lat) && !isNaN(lng)) {
                    const wm = L.marker([lat, lng], { icon: this.waitingIcon })
                        .addTo(this.map)
                        .bindPopup(`<strong>${group.name} - Waiting Location</strong>`)
                    this.bindHoverPopup(wm)
                    this.mapMarkers.push(wm)
                    all.push([lat, lng])
                }
            }

            if (all.length) {
                this.map.fitBounds(L.latLngBounds(all).pad(0.2))
            }

            this.syncVisibleDropoffMarkers()
        },
        syncVisibleDropoffMarkers() {
            if (!this.map) return
            const bounds = this.map.getBounds()

            this.allDropoffEntries.forEach(({ orderId, stop, lat, lng }) => {
                const inView = bounds.contains([lat, lng])
                const entries = this.stopMarkerIndex.get(orderId) || []
                const existing = entries.find(e => e.stop === stop)

                if (inView && !existing) {
                    const selected = this.selectedOrderIds.includes(orderId)
                    const icon = this.buildDropoffIcon(stop, selected)
                    const marker = L.marker([lat, lng], { icon })
                        .addTo(this.map)
                        .bindPopup(`<strong>Stop #${stop.sequence}</strong><br>Dropoff<br>Lat: ${stop.latitude}<br>Lng: ${stop.longitude}`)

                    this.bindHoverPopup(marker)
                    this.setMarkerPriority(marker, selected)

                    this.mapMarkers.push(marker)
                    if (!this.stopMarkerIndex.has(orderId)) {
                        this.stopMarkerIndex.set(orderId, [])
                    }
                    this.stopMarkerIndex.get(orderId).push({ marker, stop })
                } else if (!inView && existing) {
                    this.map.removeLayer(existing.marker)
                    const idx = this.mapMarkers.indexOf(existing.marker)
                    if (idx !== -1) this.mapMarkers.splice(idx, 1)

                    const list = this.stopMarkerIndex.get(orderId)
                    const listIdx = list.indexOf(existing)
                    if (listIdx !== -1) list.splice(listIdx, 1)
                    if (list.length === 0) this.stopMarkerIndex.delete(orderId)
                }
            })
        },

        updateMarkerSelection(orderId) {
            if (!this.map) return

            const order = this.orders.find(o => o.id === orderId)
            if (order) {
                const rEntry = this.restaurantMarkerIndex.get(order.restaurant_id)
                if (rEntry) {
                    rEntry.marker.setIcon(this.buildRestaurantIcon(rEntry.entries))
                    this.setMarkerPriority(
                        rEntry.marker,
                        rEntry.entries.some(e => this.selectedOrderIds.includes(e.orderId))
                    )
                }
            }

            const stopEntries = this.stopMarkerIndex.get(orderId) || []
            const selected = this.selectedOrderIds.includes(orderId)
            stopEntries.forEach(({ marker, stop }) => {
                marker.setIcon(this.buildDropoffIcon(stop, selected))
                this.setMarkerPriority(marker, selected)
            })
        },

        // Pans the map to a stop and opens its popup. Used while reordering, where
        // a card click means "where is this?" rather than "select this".
        focusStopOnMap(stop) {
            if (!this.map || !stop) return

            let lat
            let lng
            let marker = null

            if (stop.type === 'pickup') {
                const rid = this.stopRestaurantId(stop)
                const restaurant = rid ? this.restaurantsMap[rid] : null
                if (!restaurant) return
                lat = Number(restaurant.map_latitude)
                lng = Number(restaurant.map_longitude)
                const entry = this.restaurantMarkerIndex.get(restaurant.id)
                marker = entry ? entry.marker : null
            } else {
                lat = Number(stop.latitude)
                lng = Number(stop.longitude)
            }

            if (isNaN(lat) || isNaN(lng)) return

            this.focusedStopKey = this.stopKey(stop)

            this.map.setView([lat, lng], Math.max(this.map.getZoom(), FOCUS_MIN_ZOOM), { animate: true })

            if (this._focusTimer) clearTimeout(this._focusTimer)
            this._focusTimer = setTimeout(() => {
                this._focusTimer = null
                if (!this.map) return

                // The dropoff marker may not have existed before the pan
                this.syncVisibleDropoffMarkers()

                if (!marker) {
                    const entries = this.stopMarkerIndex.get(stop.order_id) || []
                    const found =
                        entries.find(e => e.stop.id != null && +e.stop.id === +stop.id) ||
                        entries.find(e => e.stop.type === stop.type)
                    marker = found ? found.marker : null
                }

                if (!marker) return

                if (this._activePopupMarker && this._activePopupMarker !== marker) {
                    this._activePopupMarker.closePopup()
                }
                this._activePopupMarker = marker
                marker.openPopup()
            }, FOCUS_REVEAL_DELAY_MS)
        },

        stopKey(stop) {
            if (!stop) return null
            return stop.id != null ? `id:${stop.id}` : `${stop.order_id}-${stop.type}`
        },

        // While reordering a card click locates the stop; otherwise it toggles selection.
        onStopCardClick(group, stop) {
            if (this.isReordering(group.route.id)) {
                this.focusStopOnMap(stop)
            } else {
                this.toggleOrder(stop.order_id)
            }
        },

        // A stop the driver has already started. Its position is fixed: it stays
        // above every pending stop, and nothing may be dropped ahead of it.
        isStopInProgress(stop) {
            return Number(stop.status) === ORDER_STATUS.IN_PROGRESS
        },

        isLockedStop(group, index) {
            return this.isReordering(group.route.id) && index < this.reorderLockedCount
        },

        // Locked stops can only swap among themselves, and only when there's
        // more than one; pending stops can only land below the locked block.
        isStopDraggable(group, index) {
            if (!this.isReordering(group.route.id)) return false
            if (index < this.reorderLockedCount) return this.reorderLockedCount > 1
            return true
        },

        canDropAt(index) {
            const from = this.reorderDragFromIndex
            if (from === null || from === undefined) return false
            const locked = this.reorderLockedCount
            return from < locked ? index < locked : index >= locked
        },

        isDropTarget(group, index) {
            return (
                this.isReordering(group.route.id) &&
                this.reorderDragOverIndex === index &&
                this.reorderDragFromIndex !== null &&
                this.reorderDragFromIndex !== index &&
                this.canDropAt(index)
            )
        },

        isDropBlocked(group, index) {
            return (
                this.isReordering(group.route.id) &&
                this.reorderDragOverIndex === index &&
                this.reorderDragFromIndex !== null &&
                this.reorderDragFromIndex !== index &&
                !this.canDropAt(index)
            )
        },

        isLockDividerAfter(group, index) {
            return (
                this.isReordering(group.route.id) &&
                this.reorderLockedCount > 0 &&
                index === this.reorderLockedCount - 1 &&
                this.reorderDraftStops.length > this.reorderLockedCount
            )
        },

        resetReorderState() {
            this.stopDragAutoScroll()
            this.reorderRouteId = null
            this.reorderDraftStops = []
            this.reorderDragFromIndex = null
            this.reorderDragOverIndex = null
            this.reorderSequenceOffset = 0
            this.reorderLockedCount = 0
            this.focusedStopKey = null
        },

        // order display
        formatDate(date) {
            return new Date(date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
            })
        },

        formatMoney(value) {
            return '£' + Number(value || 0).toFixed(2)
        },
        formatTime(date) {
            return new Date(date).toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Europe/London',
            })
        },
        formatEta(stop) {
            if (!stop || !stop.eta) return ''
            return String(stop.eta).slice(0, 5)
        },

        statusLabel(status) {
            return STATUS_LABEL[Number(status)] || ''
        },
        stopStatusLabel(stop) {
            const routeFullyClosed = stop.route && Number(stop.route.status_in_progress) === ROUTE_PROGRESS_STATUS.COMPLETED
            if (!routeFullyClosed && Number(stop.status) === ORDER_STATUS.COMPLETED) {
                return stop.type === 'pickup' ? 'Collected' : 'Delivered'
            }
            return this.statusLabel(stop.status)
        },

        statusClass(status) {
            return STATUS_CLASS[Number(status)] || ''
        },

        toggleOrder(orderId) {
            const idx = this.selectedOrderIds.indexOf(orderId)
            if (idx === -1) {
                this.selectedOrderIds.push(orderId)
            } else {
                this.selectedOrderIds.splice(idx, 1)
            }
            this.updateMarkerSelection(orderId)
        },
        isRouteVisible(routeId) {
            return !this.collapsedRouteIds.includes(routeId)
        },
        toggleRouteVisibility(routeId) {
            const idx = this.collapsedRouteIds.indexOf(routeId)
            if (idx === -1) {
                this.collapsedRouteIds.push(routeId)
            } else {
                this.collapsedRouteIds.splice(idx, 1)
            }
        },

        canReorderRoute(group) {
            if (!group || !group.route) return false
            if (this.activeTab !== TAB_KEY[ORDER_STATUS.IN_PROGRESS]) return false
            const stops = group.stops || []
            return stops.length > 1
        },
        completedStopCount(route) {
            return ((route && route.stops) || []).filter(stop => Number(stop.status) === ORDER_STATUS.COMPLETED).length
        },
        isReordering(routeId) {
            return this.reorderRouteId === routeId
        },
        stopsForDisplay(group) {
            return this.isReordering(group.route.id) ? this.reorderDraftStops : group.stops
        },
        startReorder(group) {
            if (!this.canReorderRoute(group)) return

            const stops = group.stops.map(stop => ({ ...stop }))
            const inProgress = stops.filter(stop => this.isStopInProgress(stop))
            const rest = stops.filter(stop => !this.isStopInProgress(stop))
            const offset = this.completedStopCount(group.route)

            this.reorderRouteId = group.route.id
            this.reorderSequenceOffset = offset
            this.reorderLockedCount = inProgress.length
            this.reorderDraftStops = [...inProgress, ...rest]
                .map((stop, i) => ({ ...stop, sequence: offset + i + 1 }))
            this.reorderDragFromIndex = null
            this.reorderDragOverIndex = null
            this.focusedStopKey = null
        },
        cancelReorder() {
            this.resetReorderState()
        },
        onStopDragStart(index, event) {
            this.reorderDragFromIndex = index
            this.reorderDragOverIndex = null
            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move'
                event.dataTransfer.setData('text/plain', String(index))
            }
            this.startDragAutoScroll(event ? (event.currentTarget || event.target) : null)
        },
        // No dragleave counterpart on purpose — it fires when the cursor crosses
        // into a child element and makes the badge flicker. Each card claims the
        // index on its own dragover instead. preventDefault is withheld on an
        // illegal target, which is what stops the drop from firing there.
        onStopDragOver(event, index) {
            if (index !== undefined && this.reorderDragOverIndex !== index) {
                this.reorderDragOverIndex = index
            }
            if (!event) return

            if (this.canDropAt(index)) {
                event.preventDefault()
                if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
            } else if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'none'
            }
        },
        onStopDrop(targetIndex, event) {
            if (event) event.preventDefault()
            const fromIndex = this.reorderDragFromIndex
            this.reorderDragFromIndex = null
            this.reorderDragOverIndex = null
            if (fromIndex === null || fromIndex === undefined || fromIndex === targetIndex) return

            const locked = this.reorderLockedCount
            const legal = fromIndex < locked ? targetIndex < locked : targetIndex >= locked
            if (!legal) return

            const stops = [...this.reorderDraftStops]
            const [moved] = stops.splice(fromIndex, 1)
            stops.splice(targetIndex, 0, moved)
            const offset = this.reorderSequenceOffset || 0
            this.reorderDraftStops = stops.map((stop, i) => ({ ...stop, sequence: offset + i + 1 }))

            this.stopDragAutoScroll()
            this.scrollStopIntoView(moved)
        },
        onStopDragEnd() {
            this.reorderDragFromIndex = null
            this.reorderDragOverIndex = null
            this.stopDragAutoScroll()
        },

        // ---- auto-scroll of the stop list while dragging ----
        // The browser doesn't scroll inner boxes during a native drag. v-dialog renders
        // its content outside this component's $el, so the scroll box is found by walking
        // up from the dragged card to the first element that actually scrolls.
        findScrollParent(el) {
            let node = el ? el.parentElement : null
            while (node && node !== document.body && node !== document.documentElement) {
                const style = window.getComputedStyle(node)
                const scrollable = /(auto|scroll|overlay)/.test(style.overflowY)
                if (scrollable && node.scrollHeight > node.clientHeight + 1) {
                    return node
                }
                node = node.parentElement
            }
            return document.scrollingElement || document.documentElement
        },
        startDragAutoScroll(fromEl) {
            this.stopDragAutoScroll()
            this._dragScrollEl = this.findScrollParent(fromEl)
            this._dragPointerY = null
            this._onDragPointerMove = (event) => {
                if (event.clientY) this._dragPointerY = event.clientY
            }
            document.addEventListener('dragover', this._onDragPointerMove)

            const step = () => {
                const box = this._dragScrollEl
                if (box && this._dragPointerY != null) {
                    // visible part of the scroll box (the page itself = the viewport)
                    const isPage = box === document.scrollingElement || box === document.documentElement
                    const rect = isPage ? { top: 0, bottom: window.innerHeight } : box.getBoundingClientRect()
                    const top = Math.max(rect.top, 0)
                    const bottom = Math.min(rect.bottom, window.innerHeight)

                    const fromTop = this._dragPointerY - top
                    const fromBottom = bottom - this._dragPointerY
                    let delta = 0

                    if (fromTop < DRAG_SCROLL_EDGE_PX) {
                        delta = -Math.ceil(DRAG_SCROLL_MAX_SPEED * (1 - Math.max(fromTop, 0) / DRAG_SCROLL_EDGE_PX))
                    } else if (fromBottom < DRAG_SCROLL_EDGE_PX) {
                        delta = Math.ceil(DRAG_SCROLL_MAX_SPEED * (1 - Math.max(fromBottom, 0) / DRAG_SCROLL_EDGE_PX))
                    }
                    if (delta) box.scrollTop += delta
                }
                this._dragScrollFrame = requestAnimationFrame(step)
            }
            this._dragScrollFrame = requestAnimationFrame(step)
        },
        stopDragAutoScroll() {
            if (this._dragScrollFrame) {
                cancelAnimationFrame(this._dragScrollFrame)
                this._dragScrollFrame = null
            }
            if (this._onDragPointerMove) {
                document.removeEventListener('dragover', this._onDragPointerMove)
                this._onDragPointerMove = null
            }
            this._dragPointerY = null
        },
        // Keep the card that was just moved visible
        scrollStopIntoView(stop) {
            if (!stop) return
            const key = this.stopKey(stop)
            this.$nextTick(() => {
                // dialog content lives outside this.$el, so search the document
                const card = Array.from(document.querySelectorAll('.stop-card[data-stop-key]'))
                    .find(el => el.getAttribute('data-stop-key') === String(key))
                if (card) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            })
        },
        async saveReorder(group) {
            if (this.reorderSaving) return
            this.reorderSaving = true
            try {
                await this.$store.dispatch('reorderDrivershareRouteStops', {
                    route_id: group.route.id,
                    shift_id: this.activeShift.id,
                    driver_id: this.driver ? this.driver.id : this.activeShift.driver_id,
                    stops: this.reorderDraftStops.map(stop => ({ id: stop.id, sequence: stop.sequence })),
                })
                this.resetReorderState()
            } catch (err) {
                console.warn('[DriverModal] Save stop order failed:', err)
            } finally {
                this.reorderSaving = false
            }
        },

        async confirmCancelOrder(order) {
            if (!order) return
            if (!confirm(`Cancel Order #${order.id}? This cannot be undone.`)) return

            try {
                await this.$store.dispatch('cancelDrivershareOrder', {
                    id: order.id,
                    driver_id: this.driver ? this.driver.id : this.activeShift.driver_id,
                    shift_id: this.activeShift.id,
                })

                // Drop it from the selection so no stale marker highlight lingers
                const idx = this.selectedOrderIds.indexOf(order.id)
                if (idx !== -1) this.selectedOrderIds.splice(idx, 1)
            } catch (err) {
                console.warn('[DriverModal] Cancel order failed:', err)
            }
        },
        async closeActiveRoute() {
            if (!this.activeRoute) return
            if (!confirm(`Close Route #${this.activeRoute.id}? This cannot be undone.`)) return

            try {
                await this.$store.dispatch('closeDrivershareRoute', {
                    route_id: this.activeRoute.id,
                    shift_id: this.activeShift.id,
                })
            } catch (err) {
                console.warn('[DriverModal] Close route failed:', err)
            }
        },
    },
}
</script>

<style>
.detail-page {
    height: 80vh;
    overflow: hidden;
    background: #f4f4f4;
    border-radius: 16px;
}

/* PANEL HEADER */
.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
}

/* LEFT PANEL */
.left-panel {
    height: 100%;
    width: 390px;
    max-width: 390px;
    flex: 0 0 390px !important;
    padding: 22px 28px 22px 20px;
    background: #f4f4f4;
    border-right: 1px solid #ececec;
    overflow-y: auto;
}

/* RIGHT PANEL */
.map-panel {
    height: 100%;
    flex: 1 1 0 !important;
    min-width: 0;
    padding: 0 !important;
    display: flex;
}

/* MAP CONTAINER */
.map-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 0 16px 16px 0;
}

.map-close-btn {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1000;
}

.map-close-btn .v-btn {
    width: 40px !important;
    height: 40px !important;
    background: rgba(0, 115, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.real-map {
    width: 100%;
    height: 100%;
    z-index: 1;
}

/* LEGEND */
.map-legend {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;
    display: flex;
    gap: 10px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.95);
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(8px);
}

.dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dot.blue {
    background: #2563eb;
}

.dot.orange {
    background: #f97316;
}

.dot.gray {
    background: #6b7280;
}

.dot.green {
    background: #0a7230;
}

.dot.purple {
    background: #6b20b1;
}

/* Pulsing animation for the live dot */
@keyframes live-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(168, 85, 247, 0.5);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(168, 85, 247, 0);
    }
}

.dot.pulse {
    animation: live-pulse 1.5s ease-in-out infinite;
}

.custom-pin-marker {
    background: transparent !important;
    border: none !important;
}

.pin-marker-wrap {
    position: relative;
    width: 46px;
    height: 58px;
    transition: all .2s ease;
}

.pin-marker-img {
    position: absolute;
    left: 0;
    top: 0;
    width: 46px;
    height: 58px;
    object-fit: contain;
    transition: all .2s ease;
}

.marker-pulse::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 36px;
    height: 36px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: var(--pulse-color);
    animation: markerPulse 1.5s infinite;
    z-index: -1;
    pointer-events: none;
}

@keyframes markerPulse {
    0% {
        transform: translate(-50%, -50%) scale(.8);
        opacity: .7;
    }

    100% {
        transform: translate(-50%, -50%) scale(2);
        opacity: 0;
    }
}

/* Live tracking badge under shift time */
.live-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    font-size: 11px;
    font-weight: 600;
    color: #791fc8;
    background: #f3e8ff;
    border-radius: 20px;
    padding: 2px 8px;
}

.live-badge .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #a818dd;
    animation: live-pulse 1.5s ease-in-out infinite;
}

/* TITLE */
.group-title {
    font-size: 22px;
    font-weight: 700;
    color: #081c4d;
}

/* DRIVER CARD */
.driver-card {
    background: #efefef;
    border-radius: 18px;
    padding: 18px;
    display: flex;
    align-items: center;
    margin-bottom: 28px;
}

.driver-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: #0f2d74;
    color: white;
    font-weight: 700;
    font-size: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 14px;
    flex-shrink: 0;
}

.driver-info {
    flex: 1;
    min-width: 0;
}

.driver-name {
    font-size: 17px;
    font-weight: 700;
    color: #10214d;
    margin-bottom: 4px;
}

.driver-shift {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #666;
    font-size: 14px;
}

.driver-card .v-btn {
    min-width: 82px;
    height: 34px !important;
    border-radius: 20px;
    font-size: 13px;
    text-transform: none;
    flex-shrink: 0;
}

/* ACTIVE ROUTE BANNER */
.active-route-banner {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #0f2d74;
    color: #fff;
    border-radius: 8px;
    padding: 3px 10px;
    margin: -14px 0 16px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
}

.active-route-close-btn {
    width: 18px !important;
    height: 18px !important;
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.18) !important;
}

.active-route-banner-text {
    flex: 1;
}

/* TABS */
.status-tabs {
    display: flex;
    margin-bottom: 22px;
    border-bottom: 1px solid #ececec;
}

.tab {
    flex: 1;
    text-align: center;
    padding: 0 4px 12px;
    color: #777;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
}

.tab.active {
    color: #0f2d74;
    border-bottom: 2px solid #12b7ea;
}

/* FILTER */
.business-filter {
    margin-bottom: 22px;
}

.business-filter ::v-deep .v-input__slot {
    border-radius: 12px !important;
    min-height: 44px !important;
    background: white !important;
}

/* NO ORDERS */
.no-orders {
    text-align: center;
    color: #888;
    font-size: 14px;
    padding: 32px 0;
}

/* ROUTE GROUPING */
.route-group {
    margin-bottom: 8px;
}

.route-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 700;
    color: #0f2d74;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin: 18px 0 10px;
    padding: 0 2px;
    cursor: pointer;
}

.route-toggle-icon {
    color: #0f2d74 !important;
}

.route-reorder-btn {
    margin-right: 2px;
    color: #0f2d74 !important;
}

.route-group:first-child .route-header {
    margin-top: 0;
}

/* Reorder-mode banner: shown under a route header while its stops are being drag-reordered */
.reorder-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    background: #eef7fb;
    border: 1px dashed #11A8C9;
    border-radius: 10px;
    padding: 6px 10px;
    margin: -4px 0 10px;
    font-size: 11px;
    color: #0f2d74;
}

.reorder-banner-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
}

/* Marks the boundary between the fixed in-progress block and the draggable stops */
.reorder-lock-divider {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 2px 0 12px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: #b45309;
}

.reorder-lock-divider::before,
.reorder-lock-divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: repeating-linear-gradient(90deg, #f0c48a 0 4px, transparent 4px 8px);
}

/* Drag handle shown inline at the start of a stop card's top row, only in reorder mode */
.stop-drag-handle {
    cursor: grab;
    flex-shrink: 0;
}

/* Shown in place of the drag handle on an in-progress stop that can't move */
.stop-lock-icon {
    flex-shrink: 0;
}

.stop-card--reordering {
    cursor: grab;
    border: 1px dashed #11A8C9 !important;
}

.stop-card--reordering:active {
    cursor: grabbing;
}

/* In-progress stop during reorder: pinned above the pending stops */
.stop-card--locked {
    border: 1px solid #f0c48a !important;
    background: #fdf6ec;
    cursor: default;
}

/* The card the dragged stop would land on */
.stop-card--drop-target {
    border: 2px dashed #11A8C9 !important;
    background: #e6f7fb;
    transform: translateY(2px);
    transition: transform .12s ease, background .12s ease;
}

/* The card the dragged stop may NOT land on */
.stop-card--drop-blocked {
    border: 2px dashed #ef4444 !important;
    background: #fdeaea;
    cursor: no-drop;
}

/* "DROP HERE" pill pinned to the drop target's top edge */
.drop-here-badge {
    position: absolute;
    top: -9px;
    right: 12px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #11A8C9;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .08em;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .25);
}

.drop-blocked-badge {
    position: absolute;
    top: -9px;
    right: 12px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #ef4444;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: .08em;
    white-space: nowrap;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .25);
}

/* Card last located on the map while reordering */
.stop-card--focused {
    outline: 2px solid #11A8C9;
}

/* Hint icon shown on each stop row while reordering: tap to locate on the map */
.stop-locate-icon {
    flex-shrink: 0;
    opacity: .75;
}

/* Sequence badge shown before "Order #" on stop cards */
.stop-seq-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #0966e0;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    margin-right: 6px;
    vertical-align: middle;
    flex-shrink: 0;
}

/* Side ribbon: vertical PICKUP / DROP OFF label pinned to the card's left edge — saves height vs a top banner */
.stop-side-ribbon {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px 0 0 14px;
}

.stop-side-ribbon-text {
    writing-mode: vertical-rl;
    text-orientation: upright;
    font-size: 8px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 1.5px;
}

.stop-type-pickup {
    background: #0369a1;
}

.stop-type-dropoff {
    background: #15803d;
}

/* Icon marking the restaurant name / lat-long row — the actual pickup/dropoff icon, matching the map markers */
.location-pin-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 1px;
}

/* Top row: created badge — order number — completed badge */
.stop-top-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 8px;
}

.stop-order-id {
    display: flex;
    align-items: center;
    font-size: 13px;
    font-weight: 700;
    color: #10214d;
    white-space: nowrap;
}

/* Small pill badges, styled after the Call button: compact, rounded, with a soft shadow */
.stop-time-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 50px;
    height: 24px;
    padding: 0 10px;
    border-radius: 13px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    box-shadow: 0 2px 1px -2px rgba(0, 0, 0, .2), 0 1px 2px 0 rgba(0, 0, 0, .14), 0 1px 3px 0 rgba(0, 0, 0, .12);
    flex-shrink: 0;
}

.stop-time-badge--created {
    background: #e46102;
}

.stop-time-badge--completed {
    background: #228fc5;
}

/* Location + type + status merged onto a single row for the stop cards */
.stop-location-row {
    align-items: flex-start !important;
    justify-content: space-between;
    margin-bottom: 4px !important;
    gap: 8px;
}

.stop-location-value {
    flex: 1;
    min-width: 0;
    margin-top: 1px;
    font-size: 13px;
}

.stop-row-right {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    margin-top: 0;
}

.stop-row-right .order-status {
    font-size: 11px;
    padding: 4px 11px;
}

/* Completed tab summary: "16 deliveries done   £22.50 due" */
.deliveries-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 10px 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f1f7ff;
    font-size: 13px;
    font-weight: 700;
    color: #0f2d74;
}

.deliveries-due {
    color: #1b7f3b;
}

/* Earning row on dropoff cards */
.stop-earning-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #1b7f3b;
}


/* ETA row — shown under the location row only when the stop carries a live eta (socket-only field) */
.stop-eta-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px dashed #ddd;
    font-size: 11px;
    font-weight: 600;
    color: #0f2d74;
}

/* Stop cards get a thin colored edge (left/right/bottom) matching their type badge, plus a soft shadow.
   No top border — the badge banner already colors the top of the card. */
.stop-card {
    position: relative;
    padding: 8px 14px 8px 40px;
    margin-bottom: 12px;
    border-radius: 14px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, .08);
}

.stop-card .order-footer {
    gap: 10px;
}

.stop-card .stat-box {
    padding: 8px 10px;
    border-radius: 10px;
}

.stop-card .stat-box strong {
    font-size: 14px;
}

.stop-card .stat-box span {
    font-size: 10px;
}

.stop-card.stop-card--pickup {
    border-left: 1px solid rgba(3, 105, 161, 0.3);
    border-right: 1px solid rgba(3, 105, 161, 0.3);
    border-bottom: 1px solid rgba(3, 105, 161, 0.3);
    border-top: none;
}

.stop-card.stop-card--dropoff {
    border-left: 1px solid rgba(21, 128, 61, 0.3);
    border-right: 1px solid rgba(21, 128, 61, 0.3);
    border-bottom: 1px solid rgba(21, 128, 61, 0.3);
    border-top: none;
}

.stop-card .order-date {
    font-size: 12px;
    margin-bottom: 8px;
    padding-bottom: 8px;
}

/* ORDER CARD */
.order-card {
    background: #efefef;
    border-radius: 20px;
    padding: 24px;
    margin-bottom: 18px;
    cursor: pointer;
}

.order-card--selected {
    outline: 2px solid #11A8C9;
    background: #e6f7fb;
}

.order-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.order-header-right {
    display: flex;
    align-items: center;
    gap: 4px;
}

.order-menu-btn {
    width: 28px !important;
    height: 28px !important;
}

.order-id {
    font-size: 17px;
    font-weight: 700;
    color: #10214d;
    display: flex;
    align-items: center;
}

.order-time {
    font-size: 12px;
    font-weight: 500;
    color: #a74809;
    margin-left: 8px;
    vertical-align: middle;
}

.order-status {
    padding: 5px 14px;
    border-radius: 18px;
    font-size: 13px;
    color: white;
}

.status-active {
    background: #97a7ca;
}

.status-inprogress {
    background: #3b82f6;
}

.status-completed {
    background: #087b1d;
}

.status-cancelled {
    background: #ef4444;
}

.order-date {
    color: #777;
    font-size: 14px;
    margin-bottom: 18px;
    padding-bottom: 16px;
    border-bottom: 1px solid #dddddd;
}

.location-row {
    display: flex;
    gap: 12px;
    margin-bottom: 18px;
    align-items: flex-start;
}

.location-icon {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    margin-top: 2px;
}

.location-label {
    color: #777;
    font-size: 13px;
    margin-bottom: 2px;
}

.location-value {
    color: #10214d;
    font-size: 15px;
    font-weight: 600;
    word-break: break-word;
}

.order-footer {
    display: flex;
    gap: 14px;
}

.stat-box {
    flex: 1;
    border: 1px solid #d8d8d8;
    border-radius: 14px;
    background: white;
    padding: 12px 16px;
}

.stat-box strong {
    display: block;
    font-size: 18px;
    color: #10214d;
}

.stat-box span {
    color: #777;
    font-size: 13px;
}

.custom-stop-marker {
    background: transparent !important;
    border: none !important;
}

.stop-marker {
    position: relative;
    width: 46px;
    height: 58px;
    transition: width 0.2s ease, height 0.2s ease;
}

.stop-icon {
    position: absolute;
    left: 7px;
    top: 12px;
    width: 32px;
    height: 32px;
    transition: width 0.2s ease, height 0.2s ease, left 0.2s ease, top 0.2s ease;
}

.sequence-badge {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    min-width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #0966e0;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 22px;
    text-align: center;
    border: 2px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .4);
    z-index: 9999;
}

.pickup-sequence-row {
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 3px;
    z-index: 9999;
    max-width: 140px;
    flex-wrap: wrap;
    justify-content: center;
}

.pickup-sequence-badge {
    position: relative;
    min-width: 20px;
    height: 20px;
    padding: 0 3px;
    border-radius: 10px;
    background: #0966e0;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    line-height: 20px;
    text-align: center;
    border: 2px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .4);
    cursor: default;
    transition: transform .15s ease, background .15s ease;
}

.pickup-sequence-badge.badge-selected {
    background: #ef4444;
    transform: scale(1.4);
    z-index: 20;
}

.sequence-badge--selected {
    background: #22c55e !important;
}

.pin-marker-wrap.marker-has-selected .pin-marker-img {
    filter: drop-shadow(0 0 5px rgba(239, 68, 68, 0.65));
}

.stop-order-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.stop-id {
    font-size: 10px;
    font-weight: 600;
    color: #3e4753;
    line-height: 1;
}
</style>